/**
 * S-102 (05.10.2026, właściciel): „to powinno się dać usunąć, tylko wtedy wyraźne info, że usuwany produkt
 * zostanie odpięty od danego zestawu, i usunięcie finalnie za potwierdzeniem”.
 *
 * Jedno okno usuwania karty dla szuflady i starego okna archiwizacji. Najpierw podgląd z serwera
 * (`/usun-podglad`): oferty (blokada P5 — najpierw odepnij oferty), zestawy, od których karta zostanie
 * odpięta, i skład, jeśli karta sama jest zestawem. Dopiero „Odepnij i usuń” wysyła usunięcie z tokenem
 * podglądu — gdy skład zmienił się w międzyczasie, serwer odmawia z powodem.
 */
import { showAlert, showConfirm } from '../legacy-modals.ts';

declare const SellBaseAPI: any;

interface ZestawPodgladu { bundle_id: number; sku: string; name: string; quantity: number | null; pusty: boolean; zostanie_skladnikow: number; ofert: number; stan_przed?: number; stan_po?: number; }
interface SkladnikPodgladu { product_id: number; sku: string; name: string; quantity: number; }
export interface PodgladUsuniecia {
  product_id: number; sku: string; name: string; catalog_kind: string; ofert: number;
  zestawy: ZestawPodgladu[]; sklad: SkladnikPodgladu[]; wysylanie_stanow: boolean; token: string;
}
export interface WynikUsuniecia { warning_active_orders_refs?: number; odpieto_od_zestawow?: number[]; }

const nazwa = (sku: string, name: string): string => [sku, name].filter(Boolean).join(' — ');
const szt = (n: number | null): string => (n == null ? '' : `${n} szt`);

/** Treść okna — wydzielona, żeby test mógł ją sprawdzić bez przeglądarki. */
export function trescOknaUsuniecia(p: PodgladUsuniecia): { tytul: string; tresc: string; punkty: string[]; uwaga: string; przycisk: string } {
  const punkty: string[] = [];
  for (const z of p.zestawy) {
    const przed = Number(z.stan_przed ?? 0);
    const po = Number(z.stan_po ?? 0);
    const zmiana = z.stan_przed != null && z.stan_po != null && przed !== po;
    punkty.push(`Zestaw #${z.bundle_id} ${nazwa(z.sku, z.name)} — ta karta jest w składzie (${szt(z.quantity)}). `
      + (z.pusty ? 'Zestaw zostanie bez składu — stan 0.' : `Zostanie składników: ${z.zostanie_skladnikow}.`)
      + (zmiana ? ` Stan zestawu: ${przed} → ${po} szt.` : ''));
    // Przegląd przed PROD: usunięcie składnika, którego brakowało, PODNOSI stan zestawu — mówimy to wprost.
    if (zmiana && po > przed) punkty.push(`Uwaga: stan zestawu #${z.bundle_id} wzrośnie — sprawdź, czy zestaw da się skompletować bez tej karty.`);
    if (z.ofert > 0) {
      punkty.push(`Zestaw #${z.bundle_id} ma podpięte oferty: ${z.ofert}. `
        + (p.wysylanie_stanow
          ? 'Wysyłanie stanów jest włączone — ' + (zmiana ? `stan ${po} szt. pójdzie na marketplace.` : 'stan tych ofert zmieni się na marketplace' + (z.pusty ? ' (spadnie do 0).' : '.'))
          : 'Stan zestawu w SellBase się zmieni.'));
    }
  }
  for (const c of p.sklad) punkty.push(`Składnik #${c.product_id} ${nazwa(c.sku, c.name)} (${szt(c.quantity)}) — zostaje bez zmian.`);
  if (p.zestawy.length) {
    return {
      tytul: 'Usuń produkt ze składu zestawu',
      tresc: p.zestawy.length === 1
        ? 'Usuwana karta zostanie odpięta od zestawu:'
        : `Usuwana karta zostanie odpięta od ${p.zestawy.length} zestawów:`,
      punkty,
      uwaga: 'Przywrócenie karty z archiwum NIE przywróci jej w składzie zestawu. Poprzedni skład zostaje zapisany w historii zestawu.',
      przycisk: 'Odepnij i usuń',
    };
  }
  if (p.sklad.length) {
    return {
      tytul: 'Usuń zestaw',
      tresc: 'Skład zestawu zostanie rozwiązany. Składniki zostają bez zmian:',
      punkty,
      uwaga: 'Przywrócenie zestawu z archiwum NIE przywróci jego składu. Skład zostaje zapisany w historii karty.',
      przycisk: 'Rozwiąż skład i usuń',
    };
  }
  return { tytul: 'Usuń produkt', tresc: 'Czy na pewno chcesz usunąć ten produkt? Karta trafi do archiwum.', punkty: [], uwaga: '', przycisk: 'Usuń' };
}

/** Pyta, pokazuje skutki i usuwa. `null` = anulowano albo zablokowane (komunikat już pokazany). */
export async function usunKarteZPotwierdzeniem(productId: number | string): Promise<WynikUsuniecia | null> {
  const id = encodeURIComponent(String(productId));
  const p: PodgladUsuniecia = await SellBaseAPI._request('GET', `/api/products/${id}/usun-podglad`);
  if (Number(p.ofert || 0) > 0) {
    await showAlert('Najpierw odepnij oferty', `Produkt ma ${p.ofert} podpiętych ofert. Odepnij je, a potem usuń kartę.`, { tone: 'warning' });
    return null;
  }
  const o = trescOknaUsuniecia(p);
  const zgoda = await showConfirm(o.tytul, o.tresc, { danger: true, okLabel: o.przycisk, details: o.punkty.length ? o.punkty : undefined, note: o.uwaga || undefined });
  if (!zgoda) return null;
  const powiazany = p.zestawy.length > 0 || p.sklad.length > 0;
  const qs = powiazany ? `?odepnij_od_zestawow=true&token=${encodeURIComponent(p.token)}` : '';
  return SellBaseAPI._request('DELETE', `/api/products/${id}${qs}`);
}
